import { Box, Typography, useTheme } from "@mui/material";
import CommentBulb from "./comment-bulb";
import ExpandableItemsList from "./expandable-items-list";
import {
  commentBulbBackgroundMap,
  commentBulbColorMap,
} from "@/modules/lead-page/new/constants";
import { RespondIOSessionSummaryContent } from "@/modules/lead-page/types";
import { ActivityItem } from "@/modules/lead-page/dtos/index-v2";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import Label from "@/modules/common/components/ui-kit/label";
import { formatCurrency } from "@/modules/common/utils";

interface RespondIOSessionSummaryProps {
  item: ActivityItem;
}

const RespondIOSessionSummary = ({ item }: RespondIOSessionSummaryProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();

  let parsedContent: RespondIOSessionSummaryContent | null = null;
  try {
    parsedContent = item.content ? JSON.parse(item.content) : null;
  } catch {
    parsedContent = null;
  }

  if (!parsedContent) return null;

  const hasAction = !!parsedContent.respondAction;
  const hasTargetStatus = !!parsedContent.targetStatusName;
  const hasPrice = parsedContent.askedUnitPrice != null;
  const hasTrack = !!parsedContent.trackUid;

  return (
    <>
      {hasTrack && (
        <Box
          sx={{
            display: isMobile ? "block" : "flex",
            gap: theme.nawy.spacing.xxs,
          }}
        >
          <Typography
            sx={{
              color: theme.nawy.colors.semantic.text.primary,
              ...theme.nawy.typography.english.variants.body.sm.semibold,
              minWidth: "fit-content",
            }}
          >
            Property Track:
          </Typography>
          <ExpandableItemsList
            items={[{ title: parsedContent.trackUid, link: `/property-tracks/${parsedContent.trackUid}?tab=Activity` }]}
          />
        </Box>
      )}

      {hasAction && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: theme.nawy.spacing.xxs,
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
          }}
        >
          <Typography
            sx={{
              minWidth: "84px",
              color: theme.nawy.colors.semantic.text.primary,
              ...theme.nawy.typography.english.variants.body.sm.semibold,
            }}
          >
            Requested Action:
          </Typography>
          <Label
            label={parsedContent.respondAction!.replace(/_/g, " ")}
            variant="secondary"
            color="blue"
          />

          {hasTargetStatus && (
            <>
              <Typography
                sx={{
                  color: theme.nawy.colors.semantic.text.tertiary,
                  ...theme.nawy.typography.english.variants.body.sm.regular,
                }}
              >
                triggering status update to
              </Typography>

              <Typography
                sx={{
                  color: theme.nawy.colors.semantic.text.primary,
                  ...theme.nawy.typography.english.variants.body.sm.semibold,
                }}
              >
                {parsedContent.targetStatusName}
              </Typography>
            </>
          )}
        </Box>
      )}

      {hasPrice && (
        <Box
          sx={{
            display: "flex",
            gap: theme.nawy.spacing.xxs,
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
          }}
        >
          <Typography
            sx={{
              color: theme.nawy.colors.semantic.text.primary,
              ...theme.nawy.typography.english.variants.body.sm.semibold,
              minWidth: "84px",
            }}
          >
            Requested Asking Price:
          </Typography>

          <Typography
            sx={{
              color: theme.nawy.colors.semantic.text.primary,
              ...theme.nawy.typography.english.variants.body.sm.regular,
            }}
          >
            {formatCurrency(String(parsedContent.askedUnitPrice))}
          </Typography>
        </Box>
      )}

      {parsedContent.summary && (
        <Box sx={{ marginTop: theme.nawy.spacing.xs }}>
          <CommentBulb
            content={parsedContent.summary}
            backgroundColor={commentBulbBackgroundMap.get("default")}
            color={commentBulbColorMap.get("default")}
            mentions={item.mentions}
          />
        </Box>
      )}
    </>
  );
};

export default RespondIOSessionSummary;
